
import React, { useState, useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';

interface OptimisticUpdate<T> {
  id: string;
  field: keyof T;
  value: any;
  originalValue: any;
  timestamp: number;
}

interface UseOptimisticUpdatesProps<T> {
  data: T[];
  onUpdate: (newRow: T, oldRow: T) => Promise<T>;
  debounceMs?: number;
}

export function useOptimisticUpdates<T extends { id: string }>({
  data,
  onUpdate,
  debounceMs = 800
}: UseOptimisticUpdatesProps<T>) {
  const [pendingUpdates, setPendingUpdates] = useState<Map<string, OptimisticUpdate<T>>>(new Map());
  const [fieldPendingStates, setFieldPendingStates] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const { toast } = useToast();

  // Get optimistic data with pending updates applied
  const optimisticData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.map(item => {
      let updatedItem = { ...item };
      
      // Apply all pending updates for this item in chronological order
      const itemUpdates = Array.from(pendingUpdates.values())
        .filter(update => update.id === item.id)
        .sort((a, b) => a.timestamp - b.timestamp);
      
      for (const update of itemUpdates) {
        updatedItem = {
          ...updatedItem,
          [update.field]: update.value
        };
      }
      
      return updatedItem;
    });
  }, [data, pendingUpdates]);

  const applyOptimisticUpdate = useCallback((rowId: string, field: keyof T, value: any) => {
    const originalItem = data.find(item => item.id === rowId);
    if (!originalItem) {
      console.warn(`Item with id ${rowId} not found for optimistic update`);
      return;
    }

    const updateKey = `${rowId}-${String(field)}`;
    const originalValue = originalItem[field];

    // Don't apply update if value hasn't changed
    if (originalValue === value) {
      return;
    }

    console.log(`Applying optimistic update: ${rowId} ${String(field)} = ${value}`);

    // Clear any existing timeout for this field
    const existingTimeout = timeoutRefs.current.get(updateKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      timeoutRefs.current.delete(updateKey);
    }

    // Apply optimistic update immediately
    const newUpdate: OptimisticUpdate<T> = {
      id: rowId,
      field,
      value,
      originalValue,
      timestamp: Date.now()
    };

    setPendingUpdates(prev => {
      const newUpdates = new Map(prev);
      newUpdates.set(updateKey, newUpdate);
      return newUpdates;
    });

    // Track field-level pending state
    setFieldPendingStates(prev => new Set(prev).add(updateKey));

    // Set timeout for background save
    const timeout = setTimeout(async () => {
      try {
        setIsProcessing(true);
        
        const currentItem = data.find(item => item.id === rowId);
        if (!currentItem) {
          console.warn(`Item ${rowId} no longer exists, skipping update`);
          return;
        }

        // Create updated item with the specific field change
        const updatedItem = { ...currentItem, [field]: value };

        console.log(`Saving optimistic update to backend: ${rowId} ${String(field)}`);
        await onUpdate(updatedItem, currentItem);

        // Remove pending update on success
        setPendingUpdates(prev => {
          const newUpdates = new Map(prev);
          newUpdates.delete(updateKey);
          return newUpdates;
        });

        // Remove field pending state
        setFieldPendingStates(prev => {
          const newStates = new Set(prev);
          newStates.delete(updateKey);
          return newStates;
        });

        timeoutRefs.current.delete(updateKey);
        console.log(`Successfully saved: ${rowId} ${String(field)}`);
      } catch (error) {
        console.error('Failed to save optimistic update:', error);
        
        // Rollback on error
        setPendingUpdates(prev => {
          const newUpdates = new Map(prev);
          newUpdates.delete(updateKey);
          return newUpdates;
        });

        setFieldPendingStates(prev => {
          const newStates = new Set(prev);
          newStates.delete(updateKey);
          return newStates;
        });

        toast({
          title: "Error",
          description: `Failed to save changes to ${String(field)}. Please try again.`,
          variant: "destructive"
        });
      } finally {
        setIsProcessing(false);
      }
    }, debounceMs);

    timeoutRefs.current.set(updateKey, timeout);
  }, [data, onUpdate, debounceMs, toast]);

  const isPending = useCallback((rowId: string, field?: keyof T) => {
    if (field) {
      const updateKey = `${rowId}-${String(field)}`;
      return fieldPendingStates.has(updateKey);
    }
    
    // Check if any field is pending for this row
    for (const key of fieldPendingStates) {
      if (key.startsWith(`${rowId}-`)) {
        return true;
      }
    }
    return false;
  }, [fieldPendingStates]);

  const clearPendingUpdates = useCallback(() => {
    // Clear all timeouts
    timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current.clear();
    setPendingUpdates(new Map());
    setFieldPendingStates(new Set());
  }, []);

  const flushPendingUpdates = useCallback(async () => {
    // Force save all pending updates immediately
    const updates = Array.from(pendingUpdates.values());
    
    for (const update of updates) {
      const timeout = timeoutRefs.current.get(`${update.id}-${String(update.field)}`);
      if (timeout) {
        clearTimeout(timeout);
        timeoutRefs.current.delete(`${update.id}-${String(update.field)}`);
      }
    }
    
    // Process all pending updates
    for (const update of updates) {
      const currentItem = data.find(item => item.id === update.id);
      if (currentItem) {
        try {
          const updatedItem = { ...currentItem, [update.field]: update.value };
          await onUpdate(updatedItem, currentItem);
        } catch (error) {
          console.error(`Failed to flush update for ${update.id}:`, error);
        }
      }
    }
    
    setPendingUpdates(new Map());
    setFieldPendingStates(new Set());
  }, [data, onUpdate, pendingUpdates]);

  return {
    optimisticData,
    applyOptimisticUpdate,
    isPending,
    isProcessing,
    clearPendingUpdates,
    flushPendingUpdates,
    hasPendingUpdates: pendingUpdates.size > 0
  };
}
